import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/api';
import NotificationsBell from './NotificationsBell';
import GlobalSearch from './GlobalSearch';
import { Sheet, SheetContent, SheetTitle, SheetDescription } from './ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  List,
  X,
  MapPin,
  ArrowSquareOut,
  SignOut,
  SquaresFour,
  ClipboardText,
  ChartLineUp,
  FolderOpen,
  Wallet,
  Crown,
  Stethoscope,
  UserGear,
  Kanban,
  Tag,
  Gear,
  CreditCard,
  UsersThree,
  UserPlus,
  Buildings,
  ListChecks,
  Wrench,
  ChartLine,
  DownloadSimple,
  CaretDown,
  ChatCircleText,
  FileText,
  BookOpen,
  Lock,
  User as UserIcon,
  CalendarBlank,
  Receipt,
  Star,
} from '@phosphor-icons/react';

const NAV = {
  user: [
    {
      title: 'MENU PRINCIPALE',
      items: [
        { label: 'Dashboard', to: '/dashboard', icon: SquaresFour },
        { label: 'Le mie richieste', to: '/dashboard/richieste', icon: ClipboardText },
        { label: 'Messaggi', to: '/dashboard/messaggi', icon: ChatCircleText, badge: 'unread_messages' },
        { label: 'Preventivi', to: '/dashboard/preventivi', icon: Receipt },
        { label: 'Cartella clinica', to: '/dashboard/cartella', icon: FolderOpen },
        { label: 'Promozioni', to: '/dashboard/promozioni', icon: Tag },
      ],
    },
    {
      title: 'ACCOUNT',
      items: [
        { label: 'Il mio account', to: '/account', icon: UserGear },
        { label: 'Guida', to: '/guida', icon: BookOpen },
      ],
    },
  ],
  professional: [
    {
      title: 'MENU PRINCIPALE',
      items: [
        { label: 'Dashboard', to: '/professional-dashboard', icon: SquaresFour },
        { label: 'Richieste', to: '/professional/richieste', icon: ClipboardText, badge: 'to_accept' },
        { label: 'Agenda', to: '/professional/agenda', icon: CalendarBlank, badge: 'agenda_today' },
        { label: 'Analytics', to: '/professional/analytics', icon: ChartLineUp },
        { label: 'Messaggi', to: '/professional/messaggi', icon: ChatCircleText, badge: 'unread_messages' },
        { label: 'Prestazioni', to: '/professional/prestazioni', icon: Stethoscope },
        { label: 'Recensioni', to: '/professional/recensioni', icon: Star },
        { label: 'Zona di copertura', to: '/professional/copertura', icon: MapPin },
        { label: 'Wallet', to: '/professional/wallet', icon: Wallet },
      ],
    },
    {
      title: 'ACCOUNT',
      items: [
        { label: 'Modifica profilo', to: '/professional-profile/edit', icon: UserGear },
        { label: 'Promozioni', to: '/professional/promozioni', icon: Tag },
        { label: 'Il mio account', to: '/account', icon: Lock },
        { label: 'Premium', to: '/premium', icon: Crown },
        { label: 'Guida', to: '/guida', icon: BookOpen },
      ],
    },
  ],
  admin: [
    {
      title: 'GESTIONE',
      items: [
        { label: 'Dashboard', to: '/admin', icon: SquaresFour },
        { label: 'Analytics', to: '/admin/analytics', icon: ChartLine },
        { label: 'Professionisti', to: '/admin/professionisti', icon: UserPlus },
        { label: 'Pipeline', to: '/admin/pipeline', icon: Kanban },
        { label: 'Pagamenti', to: '/admin/pagamenti', icon: CreditCard },
        { label: 'Clienti', to: '/admin/clienti', icon: UsersThree },
        { label: 'Comunicazioni', to: '/admin/messaggi', icon: ChatCircleText },
        { label: 'Centri', to: '/admin/centri', icon: Buildings },
        { label: 'Import/Export', to: '/admin/import-export', icon: DownloadSimple },
        { label: 'Tassonomia', to: '/admin/taxonomy', icon: Tag },
        { label: 'Bisogni & Zone', to: '/admin/bisogni-zone', icon: ListChecks },
        { label: 'Zone scoperte', to: '/admin/zone-scoperte', icon: MapPin },
        { label: 'Prestazioni proposte', to: '/admin/prestazioni-proposte', icon: ListChecks },
        { label: 'Strumenti', to: '/admin/strumenti', icon: Wrench },
        { label: 'Impostazioni', to: '/admin/impostazioni', icon: Gear },
        { label: 'Documentazione', to: '/admin/documentazione', icon: FileText },
      ],
    },
  ],
  center: [
    {
      title: 'MENU PRINCIPALE',
      items: [
        { label: 'Dashboard', to: '/professional-dashboard', icon: SquaresFour },
        { label: 'Richieste', to: '/professional/richieste', icon: ClipboardText, badge: 'to_accept' },
        { label: 'Preventivi', to: '/professional/preventivi', icon: Receipt },
        { label: 'Organico', to: '/professional/organico', icon: UsersThree },
        { label: 'Agenda', to: '/professional/agenda', icon: CalendarBlank, badge: 'agenda_today' },
        { label: 'Analytics', to: '/professional/analytics', icon: ChartLineUp },
        { label: 'Messaggi', to: '/professional/messaggi', icon: ChatCircleText, badge: 'unread_messages' },
        { label: 'Prestazioni', to: '/professional/prestazioni', icon: Stethoscope },
        { label: 'Recensioni', to: '/professional/recensioni', icon: Star },
        { label: 'Zona di copertura', to: '/professional/copertura', icon: MapPin },
        { label: 'Wallet', to: '/professional/wallet', icon: Wallet },
      ],
    },
    {
      title: 'ACCOUNT',
      items: [
        { label: 'Profilo del Centro', to: '/professional-profile/edit', icon: UserGear },
        { label: 'Promozioni', to: '/professional/promozioni', icon: Tag },
        { label: 'Il mio account', to: '/account', icon: Lock },
        { label: 'Premium', to: '/premium', icon: Crown },
        { label: 'Guida', to: '/guida', icon: BookOpen },
      ],
    },
  ],
  internal: [
    {
      title: 'MENU PRINCIPALE',
      items: [
        { label: 'La mia agenda', to: '/internal', icon: CalendarBlank, badge: 'to_accept' },
        { label: 'Messaggi', to: '/professional/messaggi', icon: ChatCircleText, badge: 'unread_messages' },
        { label: 'Zona di copertura', to: '/professional/copertura', icon: MapPin },
      ],
    },
    {
      title: 'ACCOUNT',
      items: [
        { label: 'La mia scheda', to: '/professional-profile/edit', icon: UserGear },
        { label: 'Il mio account', to: '/account', icon: Lock },
        { label: 'Guida', to: '/guida', icon: BookOpen },
      ],
    },
  ],
};

const ROLE_LABEL = {
  user: 'Cliente', professional: 'Professionista', admin: 'Admin',
  center: 'Centro', internal: 'Professionista del Centro',
};

const TITLES = {
  '/dashboard': 'Dashboard',
  '/dashboard/richieste': 'Le mie richieste',
  '/dashboard/nuova-richiesta': 'Nuova richiesta',
  '/dashboard/messaggi': 'Messaggi',
  '/dashboard/cartella': 'Cartella clinica',
  '/dashboard/promozioni': 'Promozioni',
  '/dashboard/preventivi': 'Preventivi',
  '/professional/preventivi': 'Preventivi',
  '/professional/recensioni': 'Recensioni',
  '/professional-dashboard': 'Dashboard',
  '/professional/richieste': 'Richieste',
  '/professional/agenda': 'Agenda',
  '/professional/messaggi': 'Messaggi',
  '/professional/prestazioni': 'Prestazioni',
  '/professional/promozioni': 'Promozioni',
  '/account': 'Il mio account',
  '/guida': 'Guida',
  '/professional/wallet': 'Wallet',
  '/professional/organico': 'Organico del Centro',
  '/professional/copertura': 'Zona di copertura',
  '/internal': 'La mia agenda',
  '/professional-profile/edit': 'Modifica profilo',
  '/admin': 'Dashboard Admin',
  '/admin/analytics': 'Analytics',
  '/admin/professionisti': 'Professionisti',
  '/admin/centri': 'Centri',
  '/admin/bisogni-zone': 'Bisogni & Zone',
  '/admin/zone-scoperte': 'Zone scoperte',
  '/admin/prestazioni-proposte': 'Prestazioni proposte',
  '/professional/completa-profilo': 'Completa il profilo',
  '/admin/strumenti': 'Strumenti',
  '/admin/crm': 'CRM Completo',
  '/admin/pipeline': 'Pipeline',
  '/admin/pagamenti': 'Pagamenti',
  '/admin/clienti': 'Clienti',
  '/admin/import-export': 'Import / Export',
  '/admin/messaggi': 'Comunicazioni',
  '/admin/taxonomy': 'Tassonomia',
  '/admin/impostazioni': 'Impostazioni',
  '/premium': 'Premium',
};

const DashboardLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [messagesUnread, setMessagesUnread] = useState(0);
  const [counters, setCounters] = useState({ to_accept: 0, unread_messages: 0, agenda_today: 0 });

  const role = user?.role || 'user';
  const navKey = user?.is_internal_pro
    ? 'internal'
    : user?.is_center && role === 'professional'
      ? 'center'
      : role;
  const groups = NAV[navKey] || NAV.user;
  const pageTitle = TITLES[location.pathname] || 'Area Riservata';
  const initials = (user?.name || user?.email || 'U').trim().charAt(0).toUpperCase();

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (role === 'admin') { setMessagesUnread(0); return; }
    let cancelled = false;
    async function loadUnread() {
      try {
        const [b, r] = await Promise.all([
          api.get('/api/conversations/unread-count').catch(() => ({ data: {} })),
          api.get('/api/request-conversations/unread-count').catch(() => ({ data: {} })),
        ]);
        if (!cancelled) setMessagesUnread(Number(b.data?.unread || 0) + Number(r.data?.unread || 0));
      } catch { /* silent */ }
    }
    loadUnread();
    const t = setInterval(loadUnread, 20000);
    const handler = () => loadUnread();
    window.addEventListener('vl:notifications-refresh', handler);
    return () => { cancelled = true; clearInterval(t); window.removeEventListener('vl:notifications-refresh', handler); };
  }, [role, location.pathname]);

  useEffect(() => {
    if (role !== 'professional') { setCounters({ to_accept: 0, unread_messages: 0, agenda_today: 0 }); return; }
    let cancelled = false;
    async function loadCounters() {
      try {
        const r = await api.get('/api/professionals/me/counters');
        if (!cancelled) setCounters(r.data || {});
      } catch { /* silent */ }
    }
    loadCounters();
    const t = setInterval(loadCounters, 60000);
    const handler = () => loadCounters();
    window.addEventListener('vl:notifications-refresh', handler);
    return () => { cancelled = true; clearInterval(t); window.removeEventListener('vl:notifications-refresh', handler); };
  }, [role, location.pathname]);

  const badgeValue = (item) => {
    if (item.badge === 'unread_messages') return messagesUnread;
    return item.badge ? Number(counters[item.badge] || 0) : 0;
  };

  const SidebarInner = ({ onNavigate }) => (
    <div className="flex flex-col h-full">
      <div className="h-24 flex items-center px-4 border-b border-[#E3F2FD]">
        <Link to="/" className="flex items-center" data-testid="sidebar-logo">
          <img src="/vivalink-logo-transparent.png" alt="VivaLink Italia" className="h-20 w-auto max-w-full" />
        </Link>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-5 space-y-6" data-testid="sidebar-nav">
        {groups.map((group) => (
          <div key={group.title}>
            <div className="px-3 mb-2 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">
              {group.title}
            </div>
            <div className="space-y-1">
              {group.items.map((item) => {
                const Icon = item.icon;
                const active = location.pathname === item.to;
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={onNavigate}
                    data-testid={`sidebar-link-${item.to.replace(/\//g, '-').replace(/^-/, '')}`}
                    className={`flex items-center gap-3 px-3 py-2.5 text-sm transition-colors duration-200 ${
                      active
                        ? 'bg-[#E3F2FD] text-primary font-semibold border-l-4 border-primary rounded-r-xl'
                        : 'text-slate-600 hover:bg-[#F5FAFF] hover:text-primary rounded-xl'
                    }`}
                  >
                    <Icon size={20} weight={active ? 'fill' : 'regular'} />
                    <span>{item.label}</span>
                    {badgeValue(item) > 0 && (
                      <span
                        className={`ml-auto min-w-[20px] h-5 rounded-full text-white text-[10px] font-bold flex items-center justify-center px-1.5 ${
                          item.badge === 'agenda_today' ? 'bg-sky-600' : 'bg-red-600'
                        }`}
                        data-testid={item.badge === 'unread_messages' ? 'sidebar-messages-unread' : `sidebar-badge-${item.badge}`}
                      >
                        {badgeValue(item) > 9 ? '9+' : badgeValue(item)}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      <div className="p-3 border-t border-[#E3F2FD]">
        <div className="flex items-center gap-3 px-2 py-2 rounded-xl bg-[#F5FAFF]">
          <div className="w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center font-heading font-bold">
            {initials}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-foreground truncate">{user?.name || 'Utente'}</div>
            <div className="text-[10px] font-semibold uppercase tracking-widest text-accent">{ROLE_LABEL[navKey] || ROLE_LABEL[role]}</div>
            {role === 'admin' && user?.admin_level && user.admin_level !== 'full' && (
              <div
                className={`mt-1 inline-block text-[10px] font-medium px-2 py-0.5 rounded-full ${user.admin_level === 'readonly' ? 'bg-amber-100 text-amber-800' : 'bg-sky-100 text-sky-800'}`}
                data-testid="admin-level-indicator"
              >
                {user.admin_level === 'readonly' ? 'Solo lettura' : 'Operativo'}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-[#F5FAFF]" data-testid="dashboard-shell">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex md:flex-col w-64 shrink-0 bg-white border-r border-[#E3F2FD] sticky top-0 h-screen">
        <SidebarInner />
      </aside>

      {/* Mobile drawer */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="p-0 w-72 bg-white [&>button]:hidden">
          <SheetTitle className="sr-only">Menu di navigazione</SheetTitle>
          <SheetDescription className="sr-only">Navigazione principale dell'area riservata VivaLink</SheetDescription>
          <SidebarInner onNavigate={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>

      {/* Main column */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 shrink-0 sticky top-0 z-40 flex items-center justify-between gap-3 px-4 md:px-8 bg-white/95 backdrop-blur-md border-b border-[#E3F2FD]" data-testid="dashboard-topbar">
          <div className="flex items-center gap-3 min-w-0">
            <button
              className="md:hidden p-2 -ml-2 rounded-lg text-slate-600 hover:bg-[#F5FAFF]"
              onClick={() => setMobileOpen(true)}
              data-testid="mobile-menu-toggle"
              aria-label="Apri menu"
            >
              <List size={22} />
            </button>
            <h1 className="font-heading font-semibold text-lg md:text-xl text-foreground truncate" data-testid="page-title">
              {pageTitle}
            </h1>
          </div>

          <div className="hidden md:flex flex-1 justify-center px-6 max-w-xl mx-auto">
            <GlobalSearch />
          </div>

          <div className="flex items-center gap-2 md:gap-3">
            <Link
              to="/"
              className="hidden sm:flex items-center gap-1.5 text-sm text-slate-500 hover:text-primary transition-colors"
              data-testid="back-to-site-link"
            >
              <ArrowSquareOut size={16} />
              <span>Torna al sito</span>
            </Link>

            <NotificationsBell />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-full hover:bg-[#F5FAFF] transition-colors"
                  data-testid="user-menu-trigger"
                >
                  <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center font-heading font-bold text-sm">
                    {initials}
                  </div>
                  <CaretDown size={14} className="text-slate-400 hidden sm:block" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="font-medium">{user?.name || 'Utente'}</div>
                  <div className="text-xs text-slate-500 font-normal">{ROLE_LABEL[role]}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {role === 'professional' && (
                  <DropdownMenuItem onClick={() => navigate('/professional-profile/edit')} data-testid="menu-profile">
                    <UserGear size={16} className="mr-2" /> Modifica profilo
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => navigate('/')} data-testid="menu-back-site">
                  <ArrowSquareOut size={16} className="mr-2" /> Torna al sito
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} className="text-red-600 focus:text-red-600" data-testid="menu-logout">
                  <SignOut size={16} className="mr-2" /> Esci
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 min-w-0" data-testid="dashboard-main">
          {children}
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
